
import { useState } from "react"
import { Link } from "react-router-dom"
import { useLocation } from "react-router-dom"
import {
  Menu, X, Package, ArrowDownToLine, ArrowUpFromLine,
  MapPin, Warehouse, Settings, ClipboardList, UserCog, ChevronDown, ChevronRight, ChevronLeft,
  Shield, Boxes, ClipboardCheck, Printer, LayoutGrid, Truck, AlertTriangle, Building2, Trash2, ArrowRightLeft, Clock,
  SlidersHorizontal, RotateCcw
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/contexts/auth-context"
import { useSidebar } from "@/contexts/sidebar-context"
import { cn } from "@/lib/utils"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useTranslations } from "@/i18n"


interface MenuItem {
  labelKey: string
  icon: typeof Package
  href: string
  badge?: boolean
  children?: MenuItem[]
  permissions?: string[]
}

interface MenuSection {
  labelKey: string
  items: MenuItem[]
}

export function Sidebar() {
  const [isOpen, setIsOpen] = useState(false)
  const { isCollapsed, toggleCollapsed } = useSidebar()
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(["outbound", "inbound", "inventoryManagement", "reportsSection", "printing", "settings"]))
  const pathname = useLocation().pathname
  const { user, isLoading, hasAnyPermission } = useAuth()
  const t = useTranslations('nav')
  const tBrand = useTranslations('brand')

  // Don't show WMS sidebar for system admins
  if (!isLoading && user?.isSystemAdmin) {
    return null
  }

  const menuStructure: (MenuItem | MenuSection)[] = [
    {
      labelKey: "outbound",
      items: [
        { labelKey: "outboundOrders", icon: ArrowUpFromLine, href: "/outbound-shipments", permissions: ['orders.view', 'orders.manage'] },
        { labelKey: "warehouseTasks", icon: ClipboardCheck, href: "/warehouse-tasks" },
      ],
    },
    {
      labelKey: "inbound",
      items: [
        { labelKey: "inboundOrders", icon: ArrowDownToLine, href: "/inbound-receiving", permissions: ['inbound.view', 'inbound.manage'] },
        { labelKey: "returns", icon: RotateCcw, href: "/returns", permissions: ['inbound.view', 'inbound.manage'] },
        { labelKey: "putaways", icon: Warehouse, href: "/putaway", permissions: ['putaway.view', 'putaway.manage'] },
      ],
    },
    {
      labelKey: "inventoryManagement",
      items: [
        { labelKey: "inventory", icon: Package, href: "/inventory", permissions: ['inventory.view', 'inventory.adjust'] },
        { labelKey: "locationInventory", icon: MapPin, href: "/inventory/locations", permissions: ['warehouses.view'] },
        { labelKey: "stockTake", icon: ClipboardCheck, href: "/stocktake", permissions: ['stocktake.view', 'stocktake.manage'] },
        { labelKey: "missingItems", icon: AlertTriangle, href: "/inventory/missing", permissions: ['inventory.view'] },
        { labelKey: "disposal", icon: Trash2, href: "/inventory/disposal", permissions: ['disposal.view', 'disposal.manage'] },
        { labelKey: "relocation", icon: ArrowRightLeft, href: "/inventory/relocation", permissions: ['relocation.view', 'relocation.manage'] },
        { labelKey: "expiry", icon: Clock, href: "/inventory/expiry", permissions: ['expiry.view'] },
      ],
    },
    {
      labelKey: "reportsSection",
      items: [
        { labelKey: "reportsInbound", icon: ArrowDownToLine, href: "/reports/inbound", permissions: ['reports.view'] },
        { labelKey: "reportsOutbound", icon: ArrowUpFromLine, href: "/reports/outbound", permissions: ['reports.view'] },
        { labelKey: "reportsInventory", icon: Package, href: "/reports/inventory", permissions: ['reports.view'] },
        { labelKey: "reportsMovement", icon: ArrowRightLeft, href: "/reports/movement", permissions: ['reports.view'] },
        { labelKey: "reportsStocktake", icon: ClipboardCheck, href: "/reports/stocktake", permissions: ['reports.view'] },
      ],
    },
    {
      labelKey: "printing",
      items: [
        { labelKey: "printJobs", icon: Printer, href: "/print-queue", permissions: ['print.view', 'print.manage'] },
      ],
    },
    {
      labelKey: "settings",
      items: [
        { labelKey: "general", icon: Settings, href: "/settings/general", permissions: ['settings.manage'] },
        { labelKey: "products", icon: ClipboardList, href: "/master-list", permissions: ['products.view', 'products.manage'], children: [
          { labelKey: "masterData", icon: ClipboardList, href: "/master-list", permissions: ['products.view', 'products.manage'] },
          { labelKey: "attributes", icon: SlidersHorizontal, href: "/settings/products/attributes", permissions: ['products.view', 'products.manage'] },
        ] },
        { labelKey: "suppliers", icon: Truck, href: "/suppliers", permissions: ['inbound.view', 'inbound.manage'] },
        { labelKey: "customers", icon: Building2, href: "/customers", permissions: ['orders.view', 'orders.manage'] },
        { labelKey: "locations", icon: LayoutGrid, href: "/settings/locations", permissions: ['warehouses.view', 'zones.manage'] },
        { labelKey: "users", icon: UserCog, href: "/users", permissions: ['users.view', 'users.create', 'users.edit'] },
        { labelKey: "printingConfig", icon: Printer, href: "/settings", permissions: ['labels.view', 'labels.manage'] },
        ...(user?.isSystemAdmin ? [{ labelKey: "adminDashboard", icon: Shield, href: "/admin" }] : []),
      ],
    },
  ]

  // Filter items by permission
  const isItemVisible = (item: MenuItem): boolean => {
    if (!item.permissions) return true
    if (user?.isSystemAdmin) return true
    return hasAnyPermission(...item.permissions)
  }

  const isActive = (href: string) => {
    if (href === "/") return pathname === "/"
    if (href === "/inventory") return pathname === "/inventory"
    if (href === "/settings") return pathname === "/settings" // Exact match only for settings
    return pathname === href || pathname.startsWith(href + "/")
  }

  const toggleSection = (sectionLabel: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev)
      if (next.has(sectionLabel)) {
        next.delete(sectionLabel)
      } else {
        next.add(sectionLabel)
      }
      return next
    })
  }

  const isSectionExpanded = (sectionLabel: string) => expandedSections.has(sectionLabel)

  return (
    <TooltipProvider delayDuration={0}>
      {/* Mobile menu button */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-4 left-4 z-50 md:hidden bg-background/80 backdrop-blur-sm shadow-sm border border-border/50"
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </Button>

      {/* Mobile overlay */}
      {isOpen && <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-30 md:hidden" onClick={() => setIsOpen(false)} />}

      {/* Sidebar wrapper - includes hover area for toggle */}
      <div
        className={cn(
          "fixed left-0 top-0 h-screen z-40 group/sidebar",
          "transition-all duration-300 ease-in-out",
          isOpen ? "w-64" : "w-0",
          isCollapsed ? "md:w-20" : "md:w-68"
        )}
      >
        {/* Sidebar */}
        <div
          className={cn(
            "h-full bg-sidebar border-r border-sidebar-border/60 flex flex-col overflow-hidden",
            "transition-all duration-300 ease-in-out",
            isOpen ? "w-64" : "w-0",
            isCollapsed ? "md:w-16" : "md:w-64"
          )}
        >
        {/* Logo/Brand */}
        <div className={cn(
          "border-b border-sidebar-border/60 transition-all duration-300",
          isCollapsed ? "px-2 py-5" : "px-5 py-5"
        )}>
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Boxes className="h-5 w-5 text-primary" />
            </div>
            {!isCollapsed && (
              <div className="overflow-hidden">
                <h1 className="text-base font-semibold text-sidebar-foreground tracking-tight whitespace-nowrap">{tBrand('name')}</h1>
                <p className="text-xs text-sidebar-foreground/50 whitespace-nowrap">{tBrand('tagline')}</p>
              </div>
            )}
          </Link>
        </div>

        {/* Navigation */}
        <nav className={cn(
          "flex-1 space-y-1 overflow-y-auto overflow-x-hidden",
          isCollapsed ? "p-2" : "p-3"
        )}>
          {menuStructure.map((item) => {
            // Single menu item
            if ("href" in item) {
              if (!isItemVisible(item)) return null
              const active = isActive(item.href)
              const label = t(item.labelKey)
              const linkContent = (
                <Link
                  key={item.labelKey}
                  to={item.href}
                  onClick={() => setIsOpen(false)}
                  className={cn(
                    "flex items-center gap-3 rounded-lg transition-all",
                    isCollapsed ? "px-2 py-2.5 justify-center" : "px-3 py-2.5",
                    active
                      ? "bg-primary/10 text-primary"
                      : "text-sidebar-foreground/75 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
                  )}
                >
                  <item.icon className={cn("w-[18px] h-[18px] flex-shrink-0", active ? 'text-primary' : 'text-sidebar-foreground/75')} />
                  {!isCollapsed && <span className={cn("text-sm whitespace-nowrap", active && 'font-medium')}>{label}</span>}
                </Link>
              )

              if (isCollapsed) {
                return (
                  <Tooltip key={item.labelKey}>
                    <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                    <TooltipContent side="right" sideOffset={10}>
                      {label}
                    </TooltipContent>
                  </Tooltip>
                )
              }
              return linkContent
            }

            // Menu section with nested items
            const section = item as MenuSection
            const visibleItems = section.items.filter(isItemVisible)
            if (visibleItems.length === 0) return null

            const sectionLabel = t(section.labelKey)
            const isExpanded = isSectionExpanded(section.labelKey)

            // When collapsed, show items directly with tooltips (flatten children)
            if (isCollapsed) {
              const flatItems = visibleItems.flatMap((child) =>
                child.children ? child.children.filter(isItemVisible) : [child]
              )
              return (
                <div key={section.labelKey} className="pt-2 first:pt-0 space-y-1">
                  {flatItems.map((child) => {
                    const active = isActive(child.href)
                    const childLabel = t(child.labelKey)
                    return (
                      <Tooltip key={child.labelKey}>
                        <TooltipTrigger asChild>
                          <Link
                            to={child.href}
                            onClick={() => setIsOpen(false)}
                            className={cn(
                              "relative flex items-center justify-center px-2 py-2.5 rounded-lg transition-all",
                              active
                                ? "bg-primary/10 text-primary"
                                : "text-sidebar-foreground/75 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
                            )}
                          >
                            <child.icon className={cn("w-[18px] h-[18px]", active ? 'text-primary' : 'text-sidebar-foreground/75')} />
                            {child.badge && (
                              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-emerald-500 rounded-full" />
                            )}
                          </Link>
                        </TooltipTrigger>
                        <TooltipContent side="right" sideOffset={10}>
                          {childLabel}
                        </TooltipContent>
                      </Tooltip>
                    )
                  })}
                </div>
              )
            }

            return (
              <div key={section.labelKey} className="pt-3 first:pt-0">
                <button
                  onClick={() => toggleSection(section.labelKey)}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors text-sidebar-foreground/60 hover:text-sidebar-foreground/80"
                >
                  <span className="text-[11px] font-semibold uppercase tracking-wider">{sectionLabel}</span>
                  {isExpanded ? (
                    <ChevronDown className="w-3.5 h-3.5 text-sidebar-foreground/60" />
                  ) : (
                    <ChevronRight className="w-3.5 h-3.5 text-sidebar-foreground/60" />
                  )}
                </button>
                {isExpanded && (
                  <div className="mt-1 space-y-1">
                    {visibleItems.map((child) => {
                      const childLabel = t(child.labelKey)

                      // Nested sub-menu
                      if (child.children) {
                        const visibleChildren = child.children.filter(isItemVisible)
                        if (visibleChildren.length === 0) return null
                        const subExpanded = isSectionExpanded(child.labelKey)
                        const anyChildActive = visibleChildren.some(c => isActive(c.href))
                        return (
                          <div key={child.labelKey}>
                            <button
                              onClick={() => toggleSection(child.labelKey)}
                              className={cn(
                                "w-full flex items-center gap-3 pl-6 pr-3 py-2 rounded-lg transition-all",
                                anyChildActive
                                  ? "text-primary"
                                  : "text-sidebar-foreground/75 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
                              )}
                            >
                              <child.icon className={cn("w-[16px] h-[16px]", anyChildActive ? 'text-primary' : 'text-sidebar-foreground/75')} />
                              <span className={cn("text-sm whitespace-nowrap flex-1 text-left", anyChildActive && 'font-medium')}>{childLabel}</span>
                              {subExpanded ? (
                                <ChevronDown className="w-3 h-3 text-sidebar-foreground/50" />
                              ) : (
                                <ChevronRight className="w-3 h-3 text-sidebar-foreground/50" />
                              )}
                            </button>
                            {subExpanded && (
                              <div className="mt-0.5 space-y-0.5">
                                {visibleChildren.map((sub) => {
                                  const subActive = isActive(sub.href)
                                  const subLabel = t(sub.labelKey)
                                  return (
                                    <Link
                                      key={sub.labelKey}
                                      to={sub.href}
                                      onClick={() => setIsOpen(false)}
                                      className={cn(
                                        "flex items-center gap-3 pl-12 pr-3 py-1.5 rounded-lg transition-all",
                                        subActive
                                          ? "bg-primary/10 text-primary"
                                          : "text-sidebar-foreground/65 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
                                      )}
                                    >
                                      <sub.icon className={cn("w-[14px] h-[14px]", subActive ? 'text-primary' : 'text-sidebar-foreground/65')} />
                                      <span className={cn("text-[13px] whitespace-nowrap", subActive && 'font-medium')}>{subLabel}</span>
                                    </Link>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        )
                      }

                      const active = isActive(child.href)
                      return (
                        <Link
                          key={child.labelKey}
                          to={child.href}
                          onClick={() => setIsOpen(false)}
                          className={cn(
                            "flex items-center gap-3 pl-6 pr-3 py-2 rounded-lg transition-all",
                            active
                              ? "bg-primary/10 text-primary"
                              : "text-sidebar-foreground/75 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
                          )}
                        >
                          <child.icon className={cn("w-[16px] h-[16px]", active ? 'text-primary' : 'text-sidebar-foreground/75')} />
                          <span className={cn("text-sm whitespace-nowrap flex items-center gap-2", active && 'font-medium')}>
                            {childLabel}
                            {child.badge && (
                              <span className="w-2 h-2 bg-emerald-500 rounded-full" />
                            )}
                          </span>
                        </Link>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </nav>

        </div>

        {/* Edge toggle */}
        <button
          onClick={toggleCollapsed}
          className={cn(
            "hidden md:flex absolute top-1/2 -translate-y-1/2 right-0 z-50",
            "h-6 w-6 items-center justify-center rounded-md",
            "bg-sidebar border border-sidebar-border/40",
            "text-sidebar-foreground/50 hover:text-sidebar-foreground",
            "opacity-0 group-hover/sidebar:opacity-100 transition-all duration-200",
            "hover:bg-sidebar-accent/50 active:scale-95"
          )}
        >
          {isCollapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </button>
      </div>
    </TooltipProvider>
  )
}
